import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { RequestService } from '../request/request';
import { Profile, ProfileDTO, UpdatePassword } from '../../models/users.interface';
import { environment } from '../../../../environments/environment';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { AsyncFormValidation } from '../../interceptors/error.interceptor';
import { Pagination } from '../../models/pagination.interface';
import { QueryGet } from '../../types/query-types';
import { AuthService } from '../auth/auth';

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  private profileKey = 'profile';
  private userProfile = new BehaviorSubject<Profile | null>(null);
  userProfile$ = this.userProfile.asObservable();

  constructor(
    private http: HttpClient,
    private requestService: RequestService,
    private authService: AuthService,
  ) {}

  private apiUrl = environment.service_1 + 'auth_user/';
  private urlUpdatePassword = environment.service_1 + 'auth_user/update-password/';

  private asyncErrorValidation = new BehaviorSubject<AsyncFormValidation | null>(null);
  asyncErrorValidation$ = this.asyncErrorValidation.asObservable();

  private profileSelected = new BehaviorSubject<Profile | null>(null);
  profileSelected$ = this.profileSelected.asObservable();

  private profiles = new BehaviorSubject<Profile[]>([]);
  profiles$ = this.profiles.asObservable();

  private myProfile = new BehaviorSubject<Profile | null>(null);
  myProfile$ = this.myProfile.asObservable();

  setProfile(profile: Profile) {
    this.profileSelected.next(profile);
  }

  getMyProfile(): void {
    const stored = localStorage.getItem('profile');
    if (stored) {
      const profile = JSON.parse(stored);
      this.myProfile.next(profile);
    } else {
      this.myProfile.next(null);
    }
  }

  setProfiles(profiles: Profile[]) {
    this.profiles.next(profiles);
  }

  removeProfile() {
    this.profileSelected.next(null);
  }

  allProfiles(query: QueryGet): Observable<Pagination<Profile[]>> {
    return this.requestService.get<Profile[]>(this.apiUrl, query);
  }

  getOneProfile(id: string) {
    return this.http.get<Profile>(`${this.apiUrl}${id}/`);
  }

  createProfile(data: ProfileDTO) {
    return this.http.post<Profile>(this.apiUrl + 'add/', data).pipe(
      tap({
        error: (err: HttpErrorResponse) => {
          this.asyncErrorValidation.next(err.error);
        },
      }),
    );
  }

  updateProfile(data: ProfileDTO, id: string) {
    return this.http.put<Profile>(this.apiUrl + `edit/${id}`, data).pipe(
      tap({
        error: (err: HttpErrorResponse) => {
          this.asyncErrorValidation.next(err.error);
        },
      }),
    );
  }

  activateProfile(id: number) {
    return this.http.post(this.apiUrl + 'activate/' + id, {}).pipe(
      tap({
        error: (err: HttpErrorResponse) => {
          this.asyncErrorValidation.next(err.error);
        },
      }),
    );
  }

  editPassword(id: number, profile: ProfileDTO) {
    return this.http.post<Profile>(this.apiUrl + 'reset-password/' + id, profile);
  }

  getUserProfile() {
    const profile = localStorage.getItem(this.profileKey);
    return profile ? (JSON.parse(profile) as Profile) : null;
  }

  revokePermissions(id: number) {
    return this.http.post(this.apiUrl + `revoke/${id}`, {});
  }

  revokeGroups(id: number) {
    return this.http.post(this.apiUrl + `revoke-group/${id}`, {});
  }

  deleteProfile(id: number) {
    return this.http.delete(this.apiUrl + `delete/${id}`);
  }

  setUserProfile(user: Profile) {
    localStorage.setItem(this.profileKey, JSON.stringify(user));
    this.userProfile.next(user);
  }

  updatePasswordProfile(profile: UpdatePassword) {
    const tokenSession = this.authService.getToken();
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + tokenSession?.access,
    });
    return this.http
      .post<Profile>(this.urlUpdatePassword, profile, {
        headers: headers,
      })
      .pipe(
        tap({
          error: (err: HttpErrorResponse) => {
            this.asyncErrorValidation.next(err.error);
          },
        }),
      );
  }
}
